import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import {
  ADMIN_CPM_CAMPAIGN_SUBMISSION_APPEALS,
  ADMIN_REVIEW_CPM_CAMPAIGN_SUBMISSION_APPEAL,
} from '@/queries/queries';
import type { AdminSubmissionAction } from '@/lib/campaigns/resolveSubmissionRefreshPlan';
import { DISAPPROVE_REASON_CHIPS } from '@/lib/campaigns/resolveDisapproveReason';
import { formatDateTime } from '@/lib/format';
import { canAcceptAppealWithImages } from '@/lib/campaigns/canAcceptAppealWithImages';
import { mapReviewImagesError } from '@/lib/campaigns/mapReviewImagesError';
import { parseReviewImages, type ReviewImage } from '@/lib/campaigns/reviewImages';
import { ReviewImagesField } from '@/components/campaigns/ReviewImagesField';
import { ReviewImagesStrip } from '@/components/campaigns/ReviewImagesStrip';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type StatusFilter = 'pending' | 'accepted' | 'kept' | 'needs_more_info' | 'all';
const STATUS_FILTERS: readonly StatusFilter[] = [
  'pending',
  'needs_more_info',
  'accepted',
  'kept',
  'all',
] as const;
const PAGE_SIZE = 25;
const REASON_CHIP_CODES = new Set<string>(
  DISAPPROVE_REASON_CHIPS.filter((chip) => chip !== 'other'),
);

type AppealRow = {
  id: string;
  submission_id: string;
  user_name?: string | null;
  user_email?: string | null;
  platform?: string | null;
  url?: string | null;
  argument?: string | null;
  extra_url?: string | null;
  evidence_image_urls?: string[] | null;
  status?: string | null;
  original_approval_reason?: string | null;
  reviewer_reply?: string | null;
  reviewer_images?: unknown;
  createdAt?: string | null;
  reviewed_at?: string | null;
};

function formatOriginalReason(
  reason: string | null | undefined,
  t: (key: string) => string,
): string {
  if (!reason) return '—';
  if (REASON_CHIP_CODES.has(reason)) {
    return t(`admin.campaigns.submissions.reasonChips.${reason}`);
  }
  return reason;
}

function formatAppealAge(createdAt: string | null | undefined): string {
  if (!createdAt) return '';
  const then = new Date(createdAt).getTime();
  if (Number.isNaN(then)) return '';
  const hours = Math.max(0, Math.floor((Date.now() - then) / 3_600_000));
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export default function CampaignAppealsTab({
  campaignId,
  refreshAfter,
}: {
  campaignId: string;
  refreshAfter: (action: AdminSubmissionAction) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [observations, setObservations] = useState<Record<string, string>>({});
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [imagesByAppeal, setImagesByAppeal] = useState<Record<string, ReviewImage[]>>({});
  const [busyImageAppeals, setBusyImageAppeals] = useState<Record<string, boolean>>({});

  const queryVars = {
    campaignId,
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };

  const { data, loading, refetch } = useQuery(ADMIN_CPM_CAMPAIGN_SUBMISSION_APPEALS, {
    variables: queryVars,
    fetchPolicy: 'cache-and-network',
  });

  const [reviewAppeal] = useMutation(ADMIN_REVIEW_CPM_CAMPAIGN_SUBMISSION_APPEAL);

  const rows: AppealRow[] = data?.adminCpmCampaignSubmissionAppeals?.data || [];
  const total = data?.adminCpmCampaignSubmissionAppeals?.total ?? 0;
  const totalPages = data?.adminCpmCampaignSubmissionAppeals?.pageCount ?? 1;

  const handleStatusFilterChange = (nextStatus: StatusFilter) => {
    setStatusFilter(nextStatus);
    setPage(1);
  };

  const replyFor = (appealId: string) => (observations[appealId] || '').trim();

  const setReplyFor = (appealId: string, value: string) => {
    setObservations((prev) => ({ ...prev, [appealId]: value }));
  };

  const setImagesFor = (appealId: string, images: ReviewImage[]) => {
    setImagesByAppeal((prev) => ({ ...prev, [appealId]: images }));
  };

  const setImagesBusyFor = (appealId: string, busy: boolean) => {
    setBusyImageAppeals((prev) => (prev[appealId] === busy ? prev : { ...prev, [appealId]: busy }));
  };

  const clearReplyFor = (appealId: string) => {
    setBusyImageAppeals((prev) => {
      const next = { ...prev };
      delete next[appealId];
      return next;
    });
    setImagesByAppeal((prev) => {
      const next = { ...prev };
      delete next[appealId];
      return next;
    });
    setObservations((prev) => {
      const next = { ...prev };
      delete next[appealId];
      return next;
    });
  };

  const onAccept = async (appealId: string) => {
    setReviewingId(appealId);
    try {
      const reply = replyFor(appealId);
      await reviewAppeal({
        variables: {
          id: appealId,
          decision: 'accept',
          reply: reply || undefined,
        },
      });
      clearReplyFor(appealId);
      await refetch();
      await refreshAfter('appealAccept');
    } catch (err: any) {
      const imagesErrorKey = mapReviewImagesError(err);
      toast({
        title: imagesErrorKey ? t(imagesErrorKey) : err?.message || t('common.error'),
        variant: 'destructive',
      });
    } finally {
      setReviewingId(null);
    }
  };

  const onKeepDecision = async (
    appealId: string,
    decision: 'keep' | 'request_more',
  ) => {
    const reply = replyFor(appealId);
    if (!reply) return;
    setReviewingId(appealId);
    try {
      await reviewAppeal({
        variables: { id: appealId, decision, reply, images: imagesByAppeal[appealId] ?? [] },
        update: (cache, { data: result }) => {
          const submissionId = result?.adminReviewCpmCampaignSubmissionAppeal?.submission_id;
          if (!submissionId) return;
          const cacheId = cache.identify({
            __typename: 'AdminCpmCampaignSubmission',
            id: submissionId,
          });
          if (!cacheId) return;
          cache.writeFragment({
            id: cacheId,
            fragment: gql`
              fragment AppealKeepPendingId on AdminCpmCampaignSubmission {
                pending_appeal_id
              }
            `,
            data: { __typename: 'AdminCpmCampaignSubmission', pending_appeal_id: null },
          });
        },
      });
      clearReplyFor(appealId);
      await refetch();
      await refreshAfter(decision === 'keep' ? 'appealKeep' : 'appealRequestMore');
    } catch (err: any) {
      const imagesErrorKey = mapReviewImagesError(err);
      toast({
        title: imagesErrorKey ? t(imagesErrorKey) : err?.message || t('common.error'),
        variant: 'destructive',
      });
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border border-border rounded-md p-1 w-fit">
        {STATUS_FILTERS.map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'ghost'}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => handleStatusFilterChange(status)}
          >
            {t(`admin.campaigns.submissions.appeals.${status}`)}
          </Button>
        ))}
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground">
          {t('admin.campaigns.submissions.appeals.empty')}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((appeal) => {
            const isReviewing = reviewingId === appeal.id;
            const age = formatAppealAge(appeal.createdAt);
            const submittedAt = formatDateTime(appeal.createdAt);
            return (
              <Card key={appeal.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                          {appeal.platform || '—'}
                        </span>
                        {age && (
                          <span
                            className="text-xs text-muted-foreground"
                            title={submittedAt}
                          >
                            {age}
                          </span>
                        )}
                      </div>
                      {appeal.url ? (
                        <a
                          href={appeal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-400 hover:underline break-all"
                        >
                          <ExternalLink className="w-3 h-3 shrink-0" />
                          {appeal.url}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground">—</p>
                      )}
                    </div>
                    <div className="text-sm text-right">
                      <p className="font-medium">{appeal.user_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{appeal.user_email || '—'}</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        {t('admin.campaigns.submissions.appeals.originalReason')}
                      </p>
                      <p>{formatOriginalReason(appeal.original_approval_reason, t)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        {t('admin.campaigns.submissions.appeals.argument')}
                      </p>
                      <p className="whitespace-pre-wrap">{appeal.argument || '—'}</p>
                    </div>
                    {appeal.extra_url && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">
                          {t('admin.campaigns.submissions.appeals.extraUrl')}
                        </p>
                        <a
                          href={appeal.extra_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-400 hover:underline break-all"
                        >
                          <ExternalLink className="w-3 h-3 shrink-0" />
                          {appeal.extra_url}
                        </a>
                      </div>
                    )}
                    {Array.isArray(appeal.evidence_image_urls)
                      && appeal.evidence_image_urls.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">
                          {t('admin.campaigns.submissions.appeals.photos')}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {appeal.evidence_image_urls.map((url) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block h-20 w-20 overflow-hidden rounded-md border border-border"
                            >
                              <img
                                src={url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {(appeal.status === 'kept' || appeal.status === 'needs_more_info' || appeal.status === 'accepted')
                      && appeal.reviewer_reply && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">
                          {t('admin.campaigns.submissions.appeals.keepReply')}
                        </p>
                        <p className="whitespace-pre-wrap">{appeal.reviewer_reply}</p>
                        <ReviewImagesStrip
                          images={parseReviewImages(appeal.reviewer_images)}
                          showVisibility
                          className="mt-1"
                        />
                      </div>
                    )}
                    {appeal.status === 'needs_more_info' && (
                      <p className="text-xs font-medium text-amber-400">
                        {t('admin.campaigns.submissions.appeals.needs_more_info')}
                      </p>
                    )}
                  </div>

                  {appeal.status === 'pending' && (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {t('admin.campaigns.submissions.appeals.observation')}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t('admin.campaigns.submissions.appeals.observationHint')}
                        </p>
                        <Textarea
                          data-testid="appeal-observation"
                          value={observations[appeal.id] || ''}
                          onChange={(e) => setReplyFor(appeal.id, e.target.value)}
                          rows={3}
                        />
                      </div>
                      <ReviewImagesField
                        value={imagesByAppeal[appeal.id] ?? []}
                        onChange={(next) => setImagesFor(appeal.id, next)}
                        disabled={isReviewing}
                        onBusyChange={(busy) => setImagesBusyFor(appeal.id, busy)}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('admin.campaigns.submissions.reviewImages.appealHint')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => onAccept(appeal.id)}
                          disabled={
                            isReviewing
                            || !canAcceptAppealWithImages(imagesByAppeal[appeal.id])
                            || !!busyImageAppeals[appeal.id]
                          }
                          data-testid="appeal-accept"
                        >
                          {isReviewing && (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          )}
                          {t('admin.campaigns.submissions.appeals.accept')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onKeepDecision(appeal.id, 'keep')}
                          disabled={isReviewing || !replyFor(appeal.id) || !!busyImageAppeals[appeal.id]}
                          data-testid="appeal-keep"
                        >
                          {t('admin.campaigns.submissions.appeals.keep')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onKeepDecision(appeal.id, 'request_more')}
                          disabled={isReviewing || !replyFor(appeal.id) || !!busyImageAppeals[appeal.id]}
                          data-testid="appeal-request-more"
                        >
                          {t('admin.campaigns.submissions.appeals.requestMore')}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                {total > 0 ? ` · ${total}` : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
